import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { watchlistApi } from '../lib/api';
import { cn, getScoreColor, getStageColor } from '../lib/utils';
import {
  Star, Trash2, Loader2, Sparkles, Users, TrendingUp,
  Building2, ArrowUpRight, Search
} from 'lucide-react';
import { Input } from '../components/ui/input';

const WatchlistPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startups, setStartups] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [removing, setRemoving] = useState(null);

  const fetchWatchlist = async () => {
    setLoading(true);
    try {
      const response = await watchlistApi.get();
      setStartups(response.data.startups || []);
    } catch (err) {
      console.error('Failed to fetch watchlist:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWatchlist();
  }, []);

  const handleRemove = async (e, startupId) => {
    e.stopPropagation();
    setRemoving(startupId);
    try {
      await watchlistApi.remove(startupId);
      setStartups(startups.filter(s => s.startup_id !== startupId));
    } catch (err) {
      console.error('Failed to remove from watchlist:', err);
    } finally {
      setRemoving(null);
    }
  };

  const filtered = searchQuery
    ? startups.filter(s =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.sector?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : startups;

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Watchlist</h1>
            <p className="text-zinc-400 font-['Manrope']">
              <span className="text-zinc-50 font-medium">{startups.length}</span> startups tracked
            </p>
          </div>
          <Button
            onClick={() => navigate('/discover')}
            data-testid="discover-from-watchlist-btn"
            className="bg-white text-zinc-900 hover:bg-zinc-200"
          >
            <Search className="w-4 h-4 mr-2" />
            Discover More
          </Button>
        </div>

        {/* Search */}
        {startups.length > 0 && (
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter watchlist..."
              data-testid="watchlist-search"
              className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-500"
            />
          </div>
        )}

        {/* Watchlist Items */}
        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
          </div>
        ) : filtered.length > 0 ? (
          <div className="space-y-3">
            {filtered.map((startup) => (
              <div
                key={startup.startup_id}
                onClick={() => navigate(`/startups/${startup.startup_id}`)}
                data-testid={`watchlist-item-${startup.startup_id}`}
                className="bg-zinc-900 border border-zinc-800 rounded-md p-4 cursor-pointer transition-all duration-200 hover:border-zinc-600 group"
              >
                <div className="flex items-center gap-4">
                  {/* Logo */}
                  <div className="w-12 h-12 bg-zinc-800 rounded-md flex items-center justify-center flex-shrink-0">
                    <span className="text-zinc-500 font-mono text-lg">{startup.name?.charAt(0)}</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-zinc-50 font-medium font-['Outfit'] text-lg truncate">{startup.name}</h3>
                      <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {startup.stage && (
                        <span className={cn("px-2 py-0.5 text-xs font-mono rounded-full border", getStageColor(startup.stage))}>
                          {startup.stage}
                        </span>
                      )}
                      {startup.sector && (
                        <span className="px-2 py-0.5 text-xs font-mono text-zinc-400 bg-zinc-800 rounded-full">
                          {startup.sector}
                        </span>
                      )}
                      {startup.source_vc && (
                        <span className="px-2 py-0.5 text-xs font-mono text-blue-400 bg-blue-400/10 rounded-full">
                          {startup.source_vc}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Scores */}
                  <div className="flex items-center gap-5 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-zinc-500 text-[10px] font-mono mb-0.5">Investor</p>
                      <p className={cn("font-mono text-sm", getScoreColor(startup.investor_respect_score || 0))}>
                        {Math.round(startup.investor_respect_score || 0)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-zinc-500 text-[10px] font-mono mb-0.5">Team</p>
                      <p className={cn("font-mono text-sm", getScoreColor(startup.team_confidence_score || 0))}>
                        {Math.round(startup.team_confidence_score || 0)}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-zinc-500 text-[10px] font-mono mb-0.5">Momentum</p>
                      <p className={cn("font-mono text-sm", getScoreColor(startup.sector_momentum_score || 0))}>
                        {Math.round(startup.sector_momentum_score || 0)}
                      </p>
                    </div>

                    {startup.conviction_score > 0 && (
                      <div className={cn("flex items-center gap-1 px-2.5 py-1 rounded-full font-mono text-sm",
                        startup.conviction_score >= 80 ? 'bg-emerald-400/10 text-emerald-400' :
                        startup.conviction_score >= 60 ? 'bg-blue-400/10 text-blue-400' :
                        'bg-amber-400/10 text-amber-400'
                      )}>
                        <Sparkles className="w-3.5 h-3.5" />
                        {Math.round(startup.conviction_score)}
                      </div>
                    )}
                  </div>

                  {/* Remove button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleRemove(e, startup.startup_id)}
                    disabled={removing === startup.startup_id}
                    data-testid={`remove-watchlist-${startup.startup_id}`}
                    className="text-zinc-600 hover:text-rose-400 hover:bg-rose-400/10 flex-shrink-0"
                  >
                    {removing === startup.startup_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-md p-16 text-center">
            <Star className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-zinc-300 font-['Outfit'] text-lg mb-2">
              {searchQuery ? 'No matches in watchlist' : 'Your watchlist is empty'}
            </h3>
            <p className="text-zinc-500 font-['Manrope'] text-sm mb-6">
              {searchQuery
                ? 'Try a different search term'
                : 'Add startups from the Discover page to track them here'}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate('/discover')}
                data-testid="empty-watchlist-discover"
                className="bg-white text-zinc-900 hover:bg-zinc-200"
              >
                Browse Startups
              </Button>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default WatchlistPage;
